import React from "react";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { useBudgetContext } from "./useBudgetContext";
import { BudgetDialog } from "./BudgetDialog";
import { GetBudgetResolutionEnum } from "@norma-bi/bi-api";
import { isAllBusiness } from "@/api";
import * as Sentry from "@sentry/react";
import { PencilIcon } from "lucide-react";
import { useAuth } from "@/Auth";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { useT } from "@/i18n";

const RESOLUTION_OPTIONS = [
  { id: GetBudgetResolutionEnum.monthly, label: "common.monthly" },
  { id: GetBudgetResolutionEnum.weekly, label: "common.weekly" },
] as const;

export function BudgetHeader() {
  const isDemo = useAuth().user?.is_demo ?? false;
  const [showEditDialog, setShowEditDialog] = React.useState(false);
  const { t, formatNumber } = useT();

  const {
    selectedYear,
    setSelectedYear,
    years,
    setSelectedBusinessId,
    selectedBusinessId,
    budgetData,
    selectedResolution,
    setSelectedResolution,
  } = useBudgetContext();
  const { data: businesses } = useGetBusinesses();

  const handleBusinessSelect = React.useCallback(
    (businessId: string) => {
      if (businessId === "ALL") {
        console.error("WARNING: Attempting to set selectedBusinessId to ALL!");
        Sentry.captureEvent({
          message: "Budget context selectedBusinessId set to ALL - investigating root cause",
          level: "error",
          tags: {
            component: "BudgetHeader",
            action: "handleBusinessSelect",
            issue: "ALL_BUSINESS_SELECTED",
          },
          extra: {
            businessId,
            businesses: businesses?.map((b) => ({ id: b.id, name: b.name })),
            selectedBusinessId,
          },
        });
        return;
      }
      setSelectedBusinessId(businessId);
    },
    [setSelectedBusinessId, businesses, selectedBusinessId],
  );

  React.useEffect(() => {
    if (businesses && !selectedBusinessId) {
      const firstNonAllBusiness = businesses.find((b) => !isAllBusiness(b.id));
      if (firstNonAllBusiness) {
        if (firstNonAllBusiness.id === "ALL") {
          console.error("WARNING: First non-ALL business is still ALL!");
          Sentry.captureEvent({
            message: "Budget context selectedBusinessId set to ALL - investigating root cause",
            level: "error",
            tags: {
              component: "BudgetHeader",
              action: "useEffect_autoSelect",
              issue: "ALL_BUSINESS_SELECTED",
            },
            extra: {
              businessId: firstNonAllBusiness.id,
              businesses: businesses?.map((b) => ({ id: b.id, name: b.name })),
              selectedBusinessId,
            },
          });
          return;
        }
        setSelectedBusinessId(firstNonAllBusiness.id);
      }
    }
  }, [businesses, selectedBusinessId, setSelectedBusinessId]);

  const showResolutionAndEdit = budgetData && budgetData.venue_budgets.length > 0;

  return (
    <>
      <SettingsPageHeader title={t("budget.title")}>
        <Select
          value={String(selectedYear)}
          onValueChange={(year) => setSelectedYear(Number(year))}
        >
          <SelectTrigger aria-label={t("budget.year")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((year) => (
              <SelectItem key={year} value={String(year)}>
                {formatNumber(year, { useGrouping: false })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showResolutionAndEdit && (
          <Select
            value={selectedResolution}
            onValueChange={(resolution) =>
              setSelectedResolution(resolution as GetBudgetResolutionEnum)
            }
          >
            <SelectTrigger aria-label={t("budget.resolution")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESOLUTION_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {t(option.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={selectedBusinessId || ""} onValueChange={handleBusinessSelect}>
          <SelectTrigger aria-label={t("common.business")} className="max-w-56">
            <SelectValue placeholder={t("common.selectBusiness")} />
          </SelectTrigger>
          <SelectContent>
            {businesses?.map((business) => (
              <SelectItem key={business.id} value={business.id}>
                <span dir="auto">{business.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showResolutionAndEdit && !isDemo && (
          <Button variant="outline" onClick={() => setShowEditDialog(true)}>
            <PencilIcon data-icon="inline-start" />
            {t("common.edit")}
          </Button>
        )}
      </SettingsPageHeader>

      {showEditDialog && !isDemo && (
        <BudgetDialog
          show={showEditDialog}
          mode="edit"
          onClose={() => setShowEditDialog(false)}
          businessId={selectedBusinessId || ""}
          year={selectedYear}
        />
      )}
    </>
  );
}
